import { useState, useEffect } from 'react';
import { CloudSun } from 'lucide-react';
import { getGeocoding, getForecast, getClimateAvg, getForecastRange } from '../services/weatherService';

interface WeatherDisplayProps {
  city: string;
  date: string;
  /** Fallback text (e.g. "Usually 27°C" from the hardcoded table, or "--") shown
   *  while loading or when all API calls fail. */
  initialWeather: string;
}

export function WeatherDisplay({ city, date, initialWeather }: WeatherDisplayProps) {
  // Live forecast (within ~16 days): actual temp + condition emoji
  const [forecast, setForecast] = useState<{ temp: number; emoji: string } | null>(null);
  // Climate average (any date): monthly avg from ERA5 reanalysis
  const [climateAvg, setClimateAvg] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchWeather = async () => {
      if (!city || city === "Gap in Itinerary" || city === "En Route") return;

      const cityName = city.split(',')[0].trim();
      setLoading(true);
      try {
        const coords = await getGeocoding(cityName);
        if (!coords || !isMounted) return;

        // 1. Try live forecast first (works up to ~16 days out)
        const fc = await getForecast(coords.lat, coords.lng, date);
        if (fc && isMounted) {
          setForecast({ temp: fc.temp, emoji: fc.emoji });
          return; // we have real data — done
        }

        // 2. Forecast unavailable (too far in the future or API hiccup) →
        //    fetch the monthly climate average for this location from ERA5.
        const monthNum = new Date(date + 'T12:00').getMonth() + 1;
        const avg = await getClimateAvg(coords.lat, coords.lng, monthNum);
        if (avg !== null && isMounted) {
          setClimateAvg(avg);
        }
      } catch (err) {
        console.error("Weather fetch failed", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWeather();
    return () => { isMounted = false; };
  }, [city, date]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (forecast) {
    return (
      <div className="flex items-center gap-2 animate-fade-in">
        <span className="text-sm">{forecast.emoji}</span>
        <span>{forecast.temp}°C</span>
      </div>
    );
  }

  if (climateAvg !== null) {
    return (
      <div className="flex items-center gap-1.5 animate-fade-in">
        <CloudSun className="w-3.5 h-3.5 opacity-70" />
        <span>Usually {climateAvg}°C</span>
      </div>
    );
  }

  // Final fallback: stored day.weather string ("Usually X°C" / "--").
  // Guard: if the stored value somehow lacks the "Usually" prefix (old Firestore
  // data created before the prefix was added), add it so the user always knows
  // they're seeing a historical average, not a live reading. Also guards against
  // initialWeather being '' or undefined (e.g. a city missing from the hardcoded
  // fallback table, or a day record with no stored weather at all) — without
  // this, the widget would render its icon next to a blank string instead of
  // a visible "no data" placeholder.
  const displayWeather = !initialWeather
    ? '--'
    : (initialWeather !== '--' && !initialWeather.startsWith('Usually '))
      ? `Usually ${initialWeather}`
      : initialWeather;

  return (
    <div className={`flex items-center gap-1.5 ${loading ? 'animate-pulse opacity-50' : ''}`}>
      <CloudSun className="w-3.5 h-3.5 opacity-70" />
      <span>{displayWeather}</span>
    </div>
  );
}

interface DestinationForecastRangeProps {
  city: string;
  /** Destination's stay — arrival and departure dates (YYYY-MM-DD). */
  startDate: string;
  endDate: string;
  /** Fallback text ("Usually 27°C" / "--") shown when no day in range has
   *  live forecast data (e.g. the whole stay is more than ~16 days out). */
  fallbackWeather: string;
}

// Destination-level weather summary for the Shortlists hero card — the min/max
// spread of forecast daily highs across the whole stay ("Forecast 20°C - 25°C"),
// rather than WeatherDisplay's single-day reading. Falls back to the same
// climate-average text Day Planner's WeatherDisplay uses once no day in the
// stay has forecast data available.
export function DestinationForecastRange({ city, startDate, endDate, fallbackWeather }: DestinationForecastRangeProps) {
  const [range, setRange] = useState<{ min: number; max: number } | null>(null);
  // Monthly climate average from ERA5 — the middle tier, for stays too far
  // out for a real forecast. Works from coordinates alone, so unlike the
  // hardcoded fallback table it covers anywhere that geocodes.
  const [climateAvg, setClimateAvg] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!city || city === "Gap in Itinerary" || city === "En Route") return;
      const cityName = city.split(',')[0].trim();
      setLoading(true);
      try {
        const coords = await getGeocoding(cityName);
        if (!coords || !isMounted) return;

        // 1. Real forecast, if the stay is close enough (~16 days).
        const r = await getForecastRange(coords.lat, coords.lng, startDate, endDate);
        if (r && isMounted) { setRange(r); return; }

        // 2. Otherwise the climate average for the month the stay starts in.
        //    WeatherDisplay (Day Planner) has always had this tier; without
        //    it here, any destination missing from the hardcoded table —
        //    Canggu, Ubud, most places outside the ~60 listed cities — fell
        //    straight through to "--" on the hero card.
        const monthNum = new Date(startDate + 'T12:00').getMonth() + 1;
        const avg = await getClimateAvg(coords.lat, coords.lng, monthNum);
        if (avg !== null && isMounted) setClimateAvg(avg);
      } catch (err) {
        console.error("Forecast range fetch failed", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => { isMounted = false; };
  }, [city, startDate, endDate]);

  if (range) {
    const label = range.min === range.max ? `${range.min}°C` : `${range.min}°C - ${range.max}°C`;
    return (
      <div className="flex items-center gap-1.5 animate-fade-in">
        <CloudSun className="w-3.5 h-3.5 opacity-70" />
        <span>{label}</span>
      </div>
    );
  }

  if (climateAvg !== null) {
    return (
      <div className="flex items-center gap-1.5 animate-fade-in">
        <CloudSun className="w-3.5 h-3.5 opacity-70" />
        <span>Usually {climateAvg}°C</span>
      </div>
    );
  }

  const displayWeather = !fallbackWeather
    ? '--'
    : (fallbackWeather !== '--' && !fallbackWeather.startsWith('Usually '))
      ? `Usually ${fallbackWeather}`
      : fallbackWeather;

  return (
    <div className={`flex items-center gap-1.5 ${loading ? 'animate-pulse opacity-50' : ''}`}>
      <CloudSun className="w-3.5 h-3.5 opacity-70" />
      <span>{displayWeather}</span>
    </div>
  );
}
